import {
  Add16,
  ChevronDown16,
  ChevronRight16,
  FolderAdd16,
  Upload16,
} from '@carbon/icons-react';
// import { Divider as AntdDivider } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { FileAttachment } from 'api/attachments';
import { UploadFileWithProgress } from 'api/files/types';
import { ProjectFileGroup } from 'api/project-files/types';
import { ReactComponent as Folder16 } from 'assets/svgs/folder-filled.svg';
import { toastService } from 'components/molecules/toast';
import { CreateGroupFormProps } from 'components/organisms/files/create-group/modal';
import { UploadFileFormProps } from 'components/organisms/projects/files/file-upload-modal';
import { useIsResponsive, useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { queryClient } from 'queries';
import {
  getProjectFileGroupsQueryKey,
  useCreateProjectFileGroupMutation,
  useLinkFilesToProjectsMutation,
  useUploadProjectFileMutation,
} from 'queries/project-files';
import { getRandomColor } from 'utils/get-random-color';
import { queueMacrotask } from 'utils/macrotask';
import { CpMenu } from '../../components/modal/cp-menu';
import { CpPopover } from '../../components/modal/cp-popover';
import { CPCreateFolderAndUploadFileModal } from '../all-files/cp-create-folder-and-upload-file-modal';
import { CpUploadFilesToFolderModal } from '../all-files/cp-upload-files-to-folder-modal';
import {
  SideBarItem,
  SideBarItemIcon,
  SideBarItemName,
  AccountsSidebar,
  ProjectWrapper,
  ProjectNameWrapper,
  ProjectNameAndChevronWrapper,
  ProjectName,
  ProjectChevronWrapper,
  ProjectEmptyState,
  AddInProjectButton,
} from '../common/side-bar';
import { ator, CpAccountFilesProps, ProjectFoldersInAccount } from './types';

const ProjectFolderListSection: React.FC<{
  projectAndFolders: ProjectFoldersInAccount;
  currentFolder?: ProjectFileGroup;
  search: string;
  onFolderClick: CpAccountFilesProps['onFolderClick'];
  onCreateAndUploadFilesClick: () => void;
  onUploadFilesClick: () => void;
}> = ({
  projectAndFolders: project,
  onFolderClick,
  currentFolder,
  search,
  onCreateAndUploadFilesClick,
  onUploadFilesClick,
}) => {
  const translate = useTranslate();
  const isResponsive = useIsResponsive();
  const [isExpanded, setIsExpanded] = useState(
    currentFolder?.projectId === project.project.projectId,
  );
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const onChevronClick = useCallback(() => {
    setIsExpanded((p) => !p);
  }, []);

  if (!project || !project?.folders) {
    return <></>;
  }
  const { folders } = project;
  const filteredFolders = [
    {
      folderId: ator(project?.project?.projectId) as unknown as number,
      folderName: translate('project.task.attachments.title', {
        count: project?.otherAttachments?.length ?? 0,
      }),
      project: project?.project,
      attachments: project?.otherAttachments,
    } as unknown as ProjectFileGroup,
  ]
    .concat?.(folders ?? [])
    ?.filter?.(
      (folder) =>
        !(
          isResponsive &&
          !!search?.trim() &&
          !folder.folderName.toLowerCase().includes(search.toLowerCase())
        ),
    );

  return (
    <ProjectWrapper isExpanded={isExpanded}>
      <ProjectNameWrapper>
        <ProjectNameAndChevronWrapper>
          <ProjectChevronWrapper onClick={onChevronClick}>
            {isExpanded ? <ChevronDown16 /> : <ChevronRight16 />}
          </ProjectChevronWrapper>
          <ProjectName>{project.project.projectName}</ProjectName>
        </ProjectNameAndChevronWrapper>
        <CpPopover
          visible={isPopoverVisible}
          onVisibleChange={setIsPopoverVisible}
          getPopupContainer={() =>
            document.getElementById('cp-modal-root') as HTMLElement
          }
          placement="bottomRight"
          content={
            <CpMenu selectedKeys={[]}>
              <CpMenu.Item
                icon={<Upload16 />}
                onClick={() => {
                  onUploadFilesClick?.();
                  setIsPopoverVisible(false);
                }}
              >
                <FormattedMessage id="upload..." />
              </CpMenu.Item>
              <CpMenu.Item
                icon={<FolderAdd16 />}
                onClick={() => {
                  onCreateAndUploadFilesClick?.();
                  setIsPopoverVisible(false);
                }}
              >
                <FormattedMessage id="project.files.create.folder.button..." />
              </CpMenu.Item>
            </CpMenu>
          }
        >
          <AddInProjectButton>
            <Add16 />
          </AddInProjectButton>
        </CpPopover>
      </ProjectNameWrapper>
      {!isExpanded ? (
        <></>
      ) : filteredFolders.length > 0 ? (
        filteredFolders.map((folder) => {
          return (
            <SideBarItem
              active={currentFolder?.folderId === folder.folderId}
              key={folder.folderId}
              onClick={() =>
                onFolderClick({
                  projectId: project.project.projectId,
                  folderId: folder.folderId,
                })
              }
            >
              <SideBarItemIcon color={getRandomColor(folder.folderName)}>
                <Folder16 />
              </SideBarItemIcon>
              <SideBarItemName>{folder.folderName}</SideBarItemName>
            </SideBarItem>
          );
        })
      ) : (
        <ProjectEmptyState>
          <FormattedMessage id="customer_portal.folders.empty_state.title" />
        </ProjectEmptyState>
      )}
    </ProjectWrapper>
  );
};

export const CpAccountFilesSidePane: React.FC<
  Pick<
    CpAccountFilesProps,
    | 'search'
    | 'currentFolder'
    | 'onFolderClick'
    | 'data'
    | 'projectId'
    | 'refetch'
  >
> = ({ search, currentFolder, onFolderClick, data, refetch }) => {
  const isResponsive = useIsTablet();
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [projectId, setProjectId] = useState<number>(-1);
  const translate = useTranslate();
  const foldersInSelectedProject = useMemo(() => {
    if (projectId === -1) {
      return [];
    }

    return data?.folders?.find((x) => x.project.projectId === projectId)
      ?.folders;
  }, [data?.folders, projectId]);

  const [_filesInProgress, setFilesInProgress] = useState<{
    [id: string]: UploadFileWithProgress;
  }>({});

  const { mutateAsync: createFileGroup, isLoading: isCreateFolderLoading } =
    useCreateProjectFileGroupMutation(['project_id', projectId]);

  const { mutateAsync: uploadFile, isLoading: isUploadingFile } =
    useUploadProjectFileMutation();

  const { mutateAsync: linkFilesToProjects, isLoading: isLinkingLoading } =
    useLinkFilesToProjectsMutation(['project_id', projectId]);

  const onFileUploadSuccess = useCallback((fileUid) => {
    queueMacrotask(() => {
      setFilesInProgress(({ [fileUid]: _, ...rest }) => ({
        ...rest,
      }));
    });
  }, []);

  const postUploadFiles = useCallback(
    async (groupId: number, fileIds: number[], fileUids: string[]) => {
      await linkFilesToProjects({
        groupId,
        fileIds,
      });

      fileUids.forEach(onFileUploadSuccess);

      queryClient.invalidateQueries(
        getProjectFileGroupsQueryKey(['project_id', projectId]),
      );
      refetch();
    },
    [linkFilesToProjects, onFileUploadSuccess, projectId, refetch],
  );

  const uploadFiles = useCallback(
    (files: UploadFile[], folderId: number) => {
      return Promise.allSettled(
        files.map(async (file): Promise<{
          file: FileAttachment;
          uid: string;
        }> => {
          setFilesInProgress((prev) => ({
            ...prev,
            [file.uid]: { ...file, progress: 1 },
          }));
          const newFile = await uploadFile({
            file,
            data: {
              projectId: projectId[1],
              folderId,
            },
            reqConfig: {
              // onUploadProgress: (event) => onUploadProgress(event, file.uid),
            },
          });

          return { file: newFile, uid: file.uid };
        }),
      );
    },
    [uploadFile, projectId],
  );

  const onCreateGroupSubmit = useCallback(
    async (formValues: CreateGroupFormProps) => {
      try {
        const newGroup = await createFileGroup(formValues);

        toastService({
          title: translate('project.folder.create.success.title'),
          message: translate('project.folder.create.success.message', {
            folderName: newGroup.folderName,
          }),
        }).success();

        try {
          const uploadResult = await uploadFiles(
            formValues.files,
            newGroup.folderId,
          );

          toastService({
            title: translate('project.files.upload.success.title'),
            message: translate('project.files.upload.success.message', {
              filesCount: uploadResult.length,
              folderName: newGroup.folderName,
            }),
          }).success();

          const fileIds: number[] = [];
          const fileUids: string[] = [];

          uploadResult.forEach((res) => {
            if (res.status === 'fulfilled') {
              fileIds.push(res.value.file.attachmentId);
              fileUids.push(res.value.uid);
            }
          });

          postUploadFiles(newGroup.folderId, fileIds, fileUids);
        } catch (e) {
          toastService({
            title: translate('project.files.upload.error.title'),
            message: translate('project.files.upload.error.message'),
          }).error();
        }
      } catch (e) {
        toastService({
          title: translate('project.folder.create.error.title'),
          message: translate('project.folder.create.error.message'),
        }).error();
      } finally {
        setIsCreateFolderModalOpen(false);
      }
    },
    [createFileGroup, translate, uploadFiles, postUploadFiles],
  );

  const onUploadFileSubmit = useCallback(
    async (formValues: UploadFileFormProps) => {
      const { group, files } = formValues;

      try {
        const uploadResult = await uploadFiles(files, Number(group.value));

        toastService({
          title: translate('project.files.upload.success.title'),
          message: translate('project.files.upload.success.message', {
            filesCount: uploadResult.length,
            folderName: group.folderName,
          }),
        }).success();

        const fileIds: number[] = [];
        const fileUids: string[] = [];

        uploadResult.forEach((res) => {
          if (res.status === 'fulfilled') {
            fileIds.push(res.value.file.attachmentId);
            fileUids.push(res.value.uid);
          }
        });

        postUploadFiles(Number(group.value), fileIds, fileUids);
      } catch (e) {
        toastService({
          title: translate('project.files.upload.error.title'),
          message: translate('project.files.upload.error.message'),
        }).error();
      } finally {
        setIsUploadModalOpen(false);
      }
    },
    [postUploadFiles, translate, uploadFiles],
  );

  const content = useMemo(
    () => (
      <>
        <AccountsSidebar>
          {data?.folders?.map((project) => {
            return (
              <ProjectFolderListSection
                currentFolder={currentFolder}
                projectAndFolders={project}
                onFolderClick={onFolderClick}
                search={search}
                onCreateAndUploadFilesClick={() => {
                  setProjectId(project?.project?.projectId);
                  queueMacrotask(() => {
                    setIsCreateFolderModalOpen(true);
                  });
                }}
                onUploadFilesClick={() => {
                  setProjectId(project?.project?.projectId);
                  queueMacrotask(() => {
                    setIsUploadModalOpen(true);
                  });
                }}
              />
            );
          })}
          {isCreateFolderModalOpen ? (
            <CPCreateFolderAndUploadFileModal
              isCreatingGroup={isCreateFolderLoading || isLinkingLoading}
              onClose={() => {
                setIsCreateFolderModalOpen(false);
                setIsUploadModalOpen(false);
              }}
              onSubmit={onCreateGroupSubmit}
              projectId={projectId}
              folders={foldersInSelectedProject ?? []}
            />
          ) : (
            <> </>
          )}
          {isUploadModalOpen ? (
            <CpUploadFilesToFolderModal
              isUploadingFile={isUploadingFile || isLinkingLoading}
              onClose={() => setIsUploadModalOpen(false)}
              projectId={projectId}
              folders={foldersInSelectedProject ?? []}
              onSubmit={onUploadFileSubmit}
            />
          ) : (
            <></>
          )}
        </AccountsSidebar>
      </>
    ),
    [
      currentFolder,
      data?.folders,
      foldersInSelectedProject,
      isCreateFolderLoading,
      isCreateFolderModalOpen,
      isLinkingLoading,
      isUploadModalOpen,
      isUploadingFile,
      onCreateGroupSubmit,
      onFolderClick,
      onUploadFileSubmit,
      projectId,
      search,
    ],
  );

  if (isResponsive) {
    return content;
  }

  return <>{!search?.trim() ? content : <></>}</>;
};
